/**
 * MetadataSelect
 * Searchable dropdown loaded from /admin/categories?type=X&section=Y
 * with inline "+" portal-based quick-add to avoid React 19 DOM conflicts.
 * Filters locally from loaded options.
 */
import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Spinner, Button } from 'react-bootstrap';
import { BsPlusCircle, BsX, BsChevronDown, BsSearch } from 'react-icons/bs';
import categoryService from '../api/categoryService';
import axiosInstance from '../api/axiosInstance';

/* ─── Portal modal — rendered at document.body to avoid DOM tree conflicts ── */
function QuickAddPortal({ show, onClose, onAdd, type, section }) {
  const inputRef = useRef();
  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);
  const [addError, setAddError] = useState('');

  // Reset when opened
  useEffect(() => {
    if (show) { setNewName(''); setAddError(''); setTimeout(() => inputRef.current?.focus(), 50); }
  }, [show]);

  if (!show) return null;

  const labelMap = { theme: 'thème', speaker: 'intervenant', video: 'catégorie vidéo', poster: 'catégorie affiche', general: 'catégorie' };
  const label = labelMap[type] || type;

  const handleSave = async () => {
    if (!newName.trim()) { setAddError('Nom requis'); return; }
    setSaving(true);
    setAddError('');
    try {
      const payload = { nom: newName.trim(), type, couleur: '#808080' };
      if (section) payload.section = section;
      const { data } = await axiosInstance.post('/admin/categories', payload);
      onAdd(data.data);
      onClose();
    } catch (err) {
      setAddError(err?.response?.data?.message || 'Erreur lors de la création');
    } finally {
      setSaving(false);
    }
  };

  return ReactDOM.createPortal(
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0,
          backgroundColor: 'rgba(0,0,0,0.45)',
          zIndex: 9998,
        }}
      />
      {/* Modal box */}
      <div style={{
        position: 'fixed', top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        backgroundColor: '#fff', borderRadius: 8,
        padding: 28, width: 340, maxWidth: '90vw',
        zIndex: 9999, boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
      }}>
        {/* Close */}
        <button
          onClick={onClose}
          style={{ position: 'absolute', top: 10, right: 14, background: 'none', border: 'none', cursor: 'pointer', color: '#888', display: 'flex', alignItems: 'center' }}
        >
          <BsX size={22} />
        </button>

        <h6 style={{ fontWeight: 700, marginBottom: 16, paddingRight: 24 }}>
          Ajouter un {label}
        </h6>

        <div>
          <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Nom :</label>
          <input
            ref={inputRef}
            type="text"
            value={newName}
            onChange={e => { setNewName(e.target.value); setAddError(''); }}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleSave(); } if (e.key === 'Escape') onClose(); }}
            placeholder={type === 'theme' ? 'Ex: Foi et espérance' : 'Ex: Frère Jean-Baptiste'}
            style={{
              width: '100%',
              padding: '6px 10px',
              border: `1px solid ${addError ? '#dc3545' : '#ced4da'}`,
              borderRadius: 4,
              fontSize: 14,
              outline: 'none',
            }}
          />
          {addError && <div style={{ color: '#dc3545', fontSize: 12, marginTop: 4 }}>{addError}</div>}
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
          <Button size="sm" variant="outline-secondary" onClick={onClose} disabled={saving}>
            Annuler
          </Button>
          <Button
            size="sm"
            disabled={saving}
            onClick={handleSave}
            style={{ backgroundColor: '#8B0000', border: 'none', minWidth: 80 }}
          >
            {saving ? <Spinner size="sm" animation="border" /> : 'Ajouter'}
          </Button>
        </div>
      </div>
    </>,
    document.body
  );
}

/* ─── Main component ─────────────────────────────────────────────────────── */
export default function MetadataSelect({ type, section, theme, value, onChange, placeholder, isInvalid, size, canAdd = true, disabled = false }) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef();
  const searchRef = useRef();
  const debounceTimer = useRef(null);

  const loadOptions = async (searchQuery = '') => {
    setLoading(true);
    try {
      const params = { type };
      // For themes: section filters by section
      // For speakers: theme prop contains the section value to filter by
      if (type === 'speaker' && theme) {
        params.section = theme;
      } else if (section) {
        params.section = section;
      }
      if (searchQuery) params.search = searchQuery;
      const { data } = await categoryService.list(params);
      setOptions(Array.isArray(data.data) ? data.data : Array.isArray(data) ? data : []);
    } catch (_) {
      setOptions([]);
    } finally {
      setLoading(false);
    }
  };

  // Initial load and when type/section/theme/disabled changes
  useEffect(() => { 
    if (disabled) {
      setOptions([]);
      return;
    }
    loadOptions(); 
  }, [type, section, theme, disabled]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced search when user types
  useEffect(() => {
    if (!open) return; // Only search when dropdown is open
    
    // Clear previous timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Set new timer for debounced search
    debounceTimer.current = setTimeout(() => {
      loadOptions(search);
    }, 300); // 300ms debounce

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [search, open]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Close on outside click ── */
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  /* ── Focus search input when dropdown opens ── */
  useEffect(() => {
    if (open) setTimeout(() => searchRef.current?.focus(), 50);
  }, [open]);

  const handleAdded = (created) => {
    setOptions(prev => [...prev, created].sort((a, b) => a.nom.localeCompare(b.nom)));
    onChange(created.nom);
  };

  const labelMap = { theme: 'thème', speaker: 'intervenant', video: 'catégorie vidéo', poster: 'catégorie affiche', general: 'catégorie' };
  const label = labelMap[type] || type;

  const height = size === 'sm' ? 31 : 38;
  const triggerLabel = value || placeholder || `— Sélectionner ${label} —`;

  const handleSelect = (nom) => {
    onChange(nom);
    setOpen(false);
    setSearch('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange('');
    setSearch('');
  };

  return (
    <>
      <div className="d-flex align-items-center gap-1">
        <div style={{ flex: 1, position: 'relative' }} ref={containerRef}>
          {/* Trigger button */}
          <div
            onClick={() => !disabled && setOpen((v) => !v)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              height,
              padding: size === 'sm' ? '0 8px' : '0 12px',
              border: `1px solid ${isInvalid ? '#dc3545' : open ? '#8B0000' : '#ced4da'}`,
              borderRadius: 4,
              backgroundColor: disabled ? '#e9ecef' : '#fff',
              cursor: disabled ? 'not-allowed' : 'pointer',
              fontSize: size === 'sm' ? 13 : 14,
              color: value ? '#212529' : '#6c757d',
              boxShadow: open ? '0 0 0 0.2rem rgba(139,0,0,.15)' : 'none',
              transition: 'border-color .15s, box-shadow .15s',
            }}
          >
            <span 
              title={triggerLabel}
              style={{ flex: 1, overflow: 'hidden', whiteSpace: 'nowrap' }}
            >
              {triggerLabel.length > 45 ? triggerLabel.slice(0, 45) + '...' : triggerLabel}
            </span>

            {/* Clear button */}
            {value && !disabled && (
              <BsX
                size={16}
                color="#6c757d"
                style={{ flexShrink: 0, cursor: 'pointer' }}
                onMouseDown={handleClear}
              />
            )}

            <BsChevronDown size={12} color="#6c757d" style={{ flexShrink: 0, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }} />
          </div>

          {/* Dropdown panel */}
          {open && (
            <div
              style={{
                position: 'absolute',
                top: height + 4,
                left: 0,
                right: 0,
                backgroundColor: '#fff',
                border: '1px solid #ced4da',
                borderRadius: 6,
                boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                zIndex: 1050,
                maxHeight: 280,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {/* Search input */}
              <div style={{ padding: '8px 10px', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', gap: 6 }}>
                <BsSearch size={13} color="#8B0000" style={{ flexShrink: 0 }} />
                <input
                  ref={searchRef}
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={`Rechercher ${label}…`}
                  style={{
                    border: 'none',
                    outline: 'none',
                    flex: 1,
                    fontSize: 13,
                    color: '#212529',
                    background: 'transparent',
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') { setOpen(false); setSearch(''); }
                    if (e.key === 'Enter' && options.length === 1) handleSelect(options[0].nom);
                  }}
                />
              </div>

              {/* Options list */}
              <div style={{ overflowY: 'auto', flex: 1 }}>
                {loading ? (
                  <div style={{ padding: '20px 12px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                    <Spinner size="sm" animation="border" style={{ color: '#8B0000' }} />
                    <span style={{ fontSize: 13, color: '#6c757d' }}>Recherche en cours…</span>
                  </div>
                ) : options.length === 0 ? (
                  <div style={{ padding: '10px 12px', color: '#aaa', fontSize: 13, textAlign: 'center' }}>
                    Aucun résultat trouvé
                  </div>
                ) : (
                  options.map((opt) => {
                    const isSelected = opt.nom === value;
                    return (
                      <div
                        key={opt.id}
                        onClick={() => handleSelect(opt.nom)}
                        style={{
                          padding: '7px 12px',
                          cursor: 'pointer',
                          fontSize: 13,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          backgroundColor: isSelected ? '#fce8e8' : 'transparent',
                          color: isSelected ? '#8B0000' : '#212529',
                          fontWeight: isSelected ? 600 : 400,
                        }}
                        onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = '#f8f9fa'; }}
                        onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent'; }}
                      >
                        {opt.couleur && (
                          <span style={{ 
                            width: 10, 
                            height: 10, 
                            borderRadius: '50%', 
                            backgroundColor: opt.couleur,
                            flexShrink: 0,
                            boxShadow: `0 0 0 2px ${opt.couleur}33`,
                          }} />
                        )}
                        <span>{opt.nom}</span>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </div>

        {/* Quick-add trigger */}
        {canAdd && !disabled && (
          <button
            type="button"
            title={`Ajouter un nouveau ${label}`}
            onClick={() => setShowAdd(true)}
            style={{
              background: 'none',
              border: '1px solid #ccc',
              borderRadius: 4,
              padding: '4px 8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              color: '#8B0000',
              flexShrink: 0,
              height,
            }}
          >
            <BsPlusCircle size={16} />
          </button>
        )}
      </div>

      <QuickAddPortal
        show={showAdd}
        onClose={() => setShowAdd(false)}
        onAdd={handleAdded}
        type={type}
        section={type === 'speaker' && theme ? theme : section}
      />
    </>
  );
}
